// src/App.jsx
import React, { useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AOS from "aos";
import "aos/dist/aos.css";
import "./styles.css";
import { FaArrowUp } from "react-icons/fa";
import Footer from "./components/Footer/Footer";
import CookieConsent from "./components/cookieConsent/CookieConsent";
import { GoogleOAuthProvider } from "@react-oauth/google";
import ChooseProfile from "Pages/ChooseProfile/ChooseProfile";
import RentalDashboard from "./Pages/RentalDashboard/RentalDashboard";
import PerformerDashboard from "./Pages/PerformerDashboard/PerformerDashboard";
import Login from "./Pages/Login/Login"; // 🆕 tuo Login-sivu
import ConfirmReservation from "./Pages/ConfirmReservationPage/ConfirmReservationPage";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

// Sivun komponentit
import Navbar from "./components/Navbar/Navbar";
import Home from "./Pages/Home/Home";
import Rental from "./Pages/Rental/Rental";
import AboutUs from "./Pages/AboutUs/AboutUs";
import UKK from "./Pages/UKK/UKK";
import Contactpage from "./Pages/Contactpage/Contactpage";
import PrivacyPolicy from "./Pages/PrivacyPolicy/PrivacyPolicy";
import BandList from "./Pages/BandList/BandList";
import BandProfile from "./components/BandProfile/BandProfile";
import NotFound from "./Pages/NotFound/NotFound";
import DetailedServices from "./Pages/DetailedServices/DetailedServices";
import ProductPage from "./Pages/ProductPage/ProductPage";
import SubscriptionOptions from "./Pages/SubscriptionOptions/SubscriptionOptions";
import Register from './Pages/Register/Register';
import CreateBandProfile from "Pages/PerformerDashboard/CreateBandProfile";

function App() {
  const location = useLocation();

  // Teema
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [theme]);

  const stripePromise = loadStripe("pk_test_51RJ8jqCHIZsgHwcfu90pwwKV10PQZlvv85lNChmGWktjjJzkfHvmJy3o9i3E4T9gJIFpE9PPArMgUkcNJzvjOkL500p3z5KxT3"); // Käytä omaa julkista Stripe-avaintasi

  // Scroll‑bar & nappi
  const [showButton, setShowButton] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const scrollHeight = document.body.scrollHeight - window.innerHeight;
      const progress = (scrollTop / scrollHeight) * 100;
      const bar = document.getElementById("bar");
      if (bar) bar.style.width = `${progress}%`;
      setShowButton(scrollTop > 500);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // AOS
  useEffect(() => {
    AOS.init({ offset: 100, duration: 400, easing: "ease-in-sine", delay: 0 });
  }, []);

  useEffect(() => {
    window.openCookieSettings = () => {
      // Avaa consent-banneri uudelleen
      localStorage.removeItem("cookieConsent");
      window.location.reload();
    };
  }, []);
  

  // Seurataan modalin tilaa sessionStoragesta
  // (BandList asettaa isModalOpen: "true" ja "false")
  const [modalOpen, setModalOpen] = useState(
    () => sessionStorage.getItem("isModalOpen") === "true"
  );
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "isModalOpen") {
        setModalOpen(sessionStorage.getItem("isModalOpen") === "true");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Navbar piilotetaan vain /esiintyjat + modal auki
  const hideNavbar = location.pathname === "/esiintyjat" && modalOpen;

  return (
    <>
      {/* Navbar */}
      {!hideNavbar && <Navbar theme={theme} setTheme={setTheme} />}

      {/* Toast */}
      <div className="fixed bottom-0 right-0 z-50">
        <ToastContainer
          position="bottom-right"
          toastClassName={() =>
            "relative flex items-center justify-center p-4 w-full max-w-sm dark:bg-violet bg-lightbg font-customFont1 text-center text-darkest dark:text-primary dark:text-shadow-lg font-normal rounded-lg animate-toast-in"
          }
          style={{
            filter: "drop-shadow(5px 5px 10px #000000)",
            transition: "transform 0.5s ease-out, opacity 0.5s ease-out",
          }}
          autoClose={6000}
          closeButton={({ closeToast }) => (
            <button
              onClick={closeToast}
              className="absolute top-2 right-2 text-red-500 hover:text-red-700"
            >
              ✖
            </button>
          )}
          progressClassName="bg-primary"
        />
      </div>

      {/* Routen taustakuva */}
      <div
        className={`bg-fixed bg-center bg-cover ${
          theme === "dark"
            ? "bg-[url('/src/assets/backgroundimage2.webp')]"
            : "bg-lightbg"
        }`}
      >
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <Elements stripe={stripePromise}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/vuokrattavat" element={<Rental />} />
          <Route path="/UKK" element={<UKK />} />
          <Route path="/meista" element={<AboutUs />} />
          <Route path="/tee-varaus" element={<Contactpage />} />
          <Route path="/esiintyjat" element={<BandList />} />
          <Route path="/esiintyja/:slug" element={<BandProfile />} />
          <Route path="/Tietosuojalausunto" element={<PrivacyPolicy />} />
          <Route path="/tuote/:slug" element={<ProductPage />} />
          <Route path="/palvelut" element={<DetailedServices />} />
          <Route path="/liity" element={<SubscriptionOptions />} />
          <Route path="/subscription-options" element={<SubscriptionOptions />} />
          <Route path="*" element={<NotFound />} />
          <Route path="/register" element={<Register />} />
          <Route path="/choose-profile" element={<ChooseProfile />} />
          <Route path="/rental-dashboard" element={<RentalDashboard />} />
          <Route path="/performer-dashboard" element={<PerformerDashboard />} />
          <Route path="/kirjaudu" element={<Login />} />
          <Route path="/vahvista-varaus" element={<ConfirmReservation />} />
          <Route path="/performer-dashboard/create" element={<CreateBandProfile />} />

        </Routes>
        </Elements>
        </GoogleOAuthProvider>
      </div>

      {/* Progress‐bar & scroll‐top */}
      <div className="progress_wrapper">
        <div className="progress_bar" id="bar"></div>
      </div>
      <FaArrowUp
        className={showButton ? "showButton" : "hidden"}
        onClick={scrollToTop}
      />

      <Footer />
      <CookieConsent />
 
      <div id="tarteaucitron" />
    </>
  );
}

export default App;
